import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ZoomInOutlined, ZoomOutOutlined, UndoOutlined, PictureOutlined, FullscreenOutlined, FullscreenExitOutlined } from "@ant-design/icons";
import { SbButton, SbEmpty, SbNotification, SbTooltip } from "uiCore";
import useFullscreenToggle from "Hooks/useFullscreenToggle";
import {
  OrgTreeCardStyled,
  OrgTreeCardHeaderStyled,
  CompareColumnHeaderStyled,
  ZoomControlsStyled,
  ZoomLevelStyled,
  ChartScrollAreaStyled,
  TreeZoomFrameStyled,
  ZoomedTreeStyled,
  OrgChartListStyled,
  TooltipTargetStyled,
} from "../orgDesign.styles";
import OrgDesignChartNode from "./OrgDesignChartNode";
import { OrgTreeScrollCorrectionProvider } from "../orgTreeScrollCorrectionContext";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import { exportNodeAsPng } from "Utils/exportPng.utils";
import { ORG_DESIGN_WORKSPACE_FROM, ORG_VIEW_EXPANDED_DEPTH } from "../orgDesign.constants";

// One tree, sharing its zoom level and scroll position with its sibling
// card via the controlled props below (see useOrgTreeViewportSync) — the
// Current and Proposed cards in OrgDesignWorkspace now pan/zoom in lockstep
// so comparing "what changed" doesn't require re-aligning each side by
// hand. Panning is native scrollLeft/scrollTop, not a CSS transform — a
// large org needs an actual scrollbar (the familiar affordance + wheel/
// trackpad scroll), and click-and-drag just moves that same scroll position
// rather than maintaining a second, competing offset. Fullscreen and PNG
// export stay entirely local to each card — those never need to affect the
// sibling. `expandAll` and `treeVersion` carry Expand all / Collapse all:
// a new treeVersion remounts the tree, opening every node when expandAll is
// set, or only the top levels (ORG_VIEW_EXPANDED_DEPTH) when it isn't. The
// ref exposes measureFit() so the workspace can work out the shared zoom
// that fits every card (see useOrgTreeViewportSync.fitToCards).
const OrgTreeCard = forwardRef(({
  title, variant, rootChildren, exportFilename, entryOption,
  zoom, animateZoom, zoomMin, zoomMax, onZoomIn, onZoomOut, onResetView, isAtDefaultView,
  scrollSync, onUserScroll, resetToken, expandAll = false, treeVersion = 0,
}, ref) => {
  const { isFullscreen, handleFullscreenToggle } = useFullscreenToggle();
  // Same screen id the workspace reports, since this card only ever renders
  // inside it — `variant` is what tells the Current and Proposed cards apart.
  const track = useAnalytics(ORG_DESIGN_WORKSPACE_FROM);
  const [isPanning, setIsPanning] = useState(false);
  const panOriginRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const scrollAreaRef = useRef(null);
  const treeRef = useRef(null);
  // The "default" scroll position is the tree centered horizontally, not
  // (0, 0) — a wide tree should open showing its middle instead of its left
  // edge. handleScroll (below) compares against this, not zero, to report
  // whether this card is currently back at its own default.
  const defaultScrollRef = useRef({ left: 0, top: 0 });
  // Guards a scroll position this card set programmatically (its own
  // centering, or an incoming scrollSync update) from being re-reported as
  // a user scroll — without this, syncing the OTHER card's move back to
  // this one would immediately bounce a "user scroll" straight back,
  // feedback-looping the two cards against each other.
  const suppressNextScrollReportRef = useRef(false);
  // What we last set scrollLeft/scrollTop to programmatically. handleScroll
  // checks the actual position against this (not a timer) to tell "this
  // event is just the echo of my own write" apart from "a real scroll raced
  // in before that echo arrived" — so a real scroll is never silently
  // dropped no matter how long the echo takes to reach us.
  const lastProgrammaticScrollRef = useRef({ left: 0, top: 0 });
  const lastScrollSyncTokenRef = useRef(scrollSync?.token ?? 0);
  // A collapse toggle's own reflow triggers the browser's native scrollLeft/
  // scrollTop clamp IMMEDIATELY — before OrgDesignRoleBoxNode's corrective
  // write even runs, two rAFs later. suppressNextScrollReportRef only ever
  // covers a single, already-known-about write, so that earlier, browser-
  // driven clamp event would sail through unsuppressed and get broadcast to
  // the sibling card before the correction had a chance to land (this is
  // what actually caused the *other* tree to jump — see beginScrollCorrection
  // below). This is a count, not a flag, so two overlapping corrections
  // (a fast double-toggle) can't have the first one's end() re-enable
  // reporting while the second one is still in flight.
  const scrollSuppressionCountRef = useRef(0);
  const hasTree = rootChildren.length > 0;
  // Sizes TreeZoomFrameStyled to the tree's scaled size so the scroll range
  // matches what's visible (offsetWidth/offsetHeight ignore the scale
  // transform). Written straight to the frame rather than through state, so
  // it lands in the same layout pass as whatever changed the size — a zoom
  // step, or Expand all / Collapse all — and the re-centering further down
  // measures the final scroll range. ResizeObserver covers a single node
  // being expanded or collapsed.
  const frameRef = useRef(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const sizeFrame = useCallback(() => {
    const tree = treeRef.current;
    const frame = frameRef.current;
    if (!tree || !frame) return;
    frame.style.width = `${tree.offsetWidth * zoomRef.current}px`;
    frame.style.height = `${tree.offsetHeight * zoomRef.current}px`;
  }, []);

  useLayoutEffect(() => {
    sizeFrame();
  }, [zoom, treeVersion, expandAll, hasTree, sizeFrame]);

  useImperativeHandle(
    ref,
    () => ({
      // The tree's unscaled width (offsetWidth ignores the scale transform)
      // and the card's visible width. null while the card shows its empty
      // state, so the fit leaves it out.
      measureFit: () => {
        const area = scrollAreaRef.current;
        const tree = treeRef.current;
        if (!area || !tree) return null;
        return { contentWidth: tree.offsetWidth, viewportWidth: area.clientWidth };
      },
    }),
    []
  );

  useLayoutEffect(() => {
    const tree = treeRef.current;
    if (!tree || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(sizeFrame);
    observer.observe(tree);
    return () => observer.disconnect();
  }, [hasTree, sizeFrame]);

  // Sets scroll position programmatically (centering, or an incoming sync
  // from the sibling card) without it bouncing back out as a "user scroll".
  const setScrollProgrammatically = useCallback((left, top) => {
    const el = scrollAreaRef.current;
    if (!el) return;
    lastProgrammaticScrollRef.current = { left, top };
    suppressNextScrollReportRef.current = true;
    el.scrollLeft = left;
    el.scrollTop = top;
  }, []);

  // Exposed to descendants (via OrgTreeScrollCorrectionProvider below) so a
  // node can silence ALL scroll reporting on this card for the entire span
  // of its own collapse-toggle reflow + corrective re-anchor (bug #1910) —
  // begin() before the toggle, end() once the correction has been measured
  // and applied. Nothing in that window, including the browser's own
  // clamp, gets mistaken for a user pan and mirrored onto the sibling card.
  const beginScrollCorrection = useCallback(() => {
    scrollSuppressionCountRef.current += 1;
  }, []);

  const endScrollCorrection = useCallback((deltaLeft, deltaTop) => {
    const el = scrollAreaRef.current;
    if (el && (deltaLeft !== 0 || deltaTop !== 0)) {
      el.scrollLeft += deltaLeft;
      el.scrollTop += deltaTop;
    }
    scrollSuppressionCountRef.current = Math.max(0, scrollSuppressionCountRef.current - 1);
  }, []);

  const scrollCorrection = useMemo(
    () => ({ begin: beginScrollCorrection, end: endScrollCorrection }),
    [beginScrollCorrection, endScrollCorrection]
  );

  const centerScroll = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const left = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
    defaultScrollRef.current = { left, top: 0 };
    setScrollProgrammatically(left, 0);
  }, [setScrollProgrammatically]);

  // Center once the tree has actually rendered at its natural size, and
  // again whenever a different tree (rootChildren) is shown.
  useEffect(() => {
    centerScroll();
  }, [rootChildren, centerScroll]);

  // Re-centers this card's own scroll position after every fit, Reset,
  // Expand all or Collapse all (see useOrgTreeViewportSync); zoom itself
  // arrives via the controlled `zoom` prop. A layout effect, declared after
  // sizeFrame's, so the frame already has its new size and the centered
  // position is in place before the chart paints.
  useLayoutEffect(() => {
    if (resetToken === undefined) return;
    centerScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  // Applies a scroll position broadcast by the sibling card. Only reacts to
  // a genuinely new token — an unrelated re-render passing the same
  // scrollSync object again must not re-apply (and potentially re-fire) it.
  // Also skips a sync this card itself just broadcast (`sourceVariant ===
  // variant`) — applying it back onto the very card that's still actively
  // scrolling/panning re-derives its position from a fraction that's already
  // stale by the time this effect runs, yanking the scrollbar back a step
  // and fighting the next in-progress scroll/mousemove event. That fight is
  // what showed up as both scrollbars visibly jittering.
  useEffect(() => {
    if (!scrollSync || scrollSync.token === lastScrollSyncTokenRef.current) return;
    lastScrollSyncTokenRef.current = scrollSync.token;
    if (scrollSync.xFrac == null || scrollSync.sourceVariant === variant) return;
    const el = scrollAreaRef.current;
    if (!el) return;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
    setScrollProgrammatically(scrollSync.xFrac * maxLeft, scrollSync.yFrac * maxTop);
  }, [scrollSync, setScrollProgrammatically, variant]);

  // Native scroll position isn't React state — this reports every real
  // (user wheel/scrollbar/drag) scroll up as a fraction of this card's own
  // scrollable range, plus whether this card is currently back at its own
  // centered default, for the parent to mirror onto the sibling card and to
  // drive the shared Reset button's disabled state. `variant` tags the
  // report with which card produced it, so that same card's own sync-apply
  // effect above can skip re-applying its own echo to itself.
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return undefined;
    const handleScroll = () => {
      // A collapse-toggle correction is in flight on this card — swallow
      // every scroll event for its whole duration (see beginScrollCorrection),
      // not just the one write we already know is coming.
      if (scrollSuppressionCountRef.current > 0) return;
      if (suppressNextScrollReportRef.current) {
        suppressNextScrollReportRef.current = false;
        const { left, top } = lastProgrammaticScrollRef.current;
        // Just the echo of our own programmatic write — nothing to report.
        // If the position doesn't match what we set, a real scroll raced in
        // before that echo arrived; fall through and report it for real
        // instead of silently dropping it.
        if (Math.abs(el.scrollLeft - left) < 1 && Math.abs(el.scrollTop - top) < 1) return;
      }
      const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
      const { left: defaultLeft, top: defaultTop } = defaultScrollRef.current;
      const atDefault = Math.abs(el.scrollLeft - defaultLeft) < 1 && Math.abs(el.scrollTop - defaultTop) < 1;
      onUserScroll?.(maxLeft > 0 ? el.scrollLeft / maxLeft : 0, maxTop > 0 ? el.scrollTop / maxTop : 0, atDefault, variant);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [onUserScroll, variant]);

  useEffect(() => {
    if (!isPanning) return undefined;
    const handleMove = (e) => {
      const el = scrollAreaRef.current;
      if (!el) return;
      el.scrollLeft = panOriginRef.current.scrollLeft - (e.clientX - panOriginRef.current.x);
      el.scrollTop = panOriginRef.current.scrollTop - (e.clientY - panOriginRef.current.y);
    };
    const handleUp = () => setIsPanning(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isPanning]);

  const handlePanStart = (e) => {
    if (e.button !== 0) return; // left click/drag only
    const el = scrollAreaRef.current;
    panOriginRef.current = { x: e.clientX, y: e.clientY, scrollLeft: el?.scrollLeft ?? 0, scrollTop: el?.scrollTop ?? 0 };
    setIsPanning(true);
  };

  // Exports the full tree at 100% regardless of the current shared zoom —
  // html2canvas captures the content node itself, not the visible scrolled
  // viewport, so scroll position doesn't matter. The zoom is removed only
  // on html2canvas's off-screen copy of the page (onclone), so the chart on
  // screen never changes while the PNG is made.
  const handleExportPng = async () => {
    const node = treeRef.current;
    if (!node) return;
    const success = await exportNodeAsPng(node, exportFilename, {
      onclone: (_clonedDocument, clonedNode) => {
        clonedNode.style.transition = "none";
        clonedNode.style.transform = "none";
      },
    });
    track(ANALYTICS_EVENTS.ORG_DESIGN.TREE_EXPORTED_PNG, { variant, succeeded: success });
    if (!success) SbNotification.error("Export failed", "Could not save this tree as a PNG. Please try again.");
  };

  if (!hasTree) {
    return (
      <OrgTreeCardStyled>
        <OrgTreeCardHeaderStyled>
          <CompareColumnHeaderStyled $variant={variant}>{title}</CompareColumnHeaderStyled>
        </OrgTreeCardHeaderStyled>
        <SbEmpty description="No roles to show." />
      </OrgTreeCardStyled>
    );
  }

  return (
    <OrgTreeCardStyled $isFullscreen={isFullscreen}>
      <OrgTreeCardHeaderStyled>
        <CompareColumnHeaderStyled $variant={variant}>{title}</CompareColumnHeaderStyled>
        <ZoomControlsStyled>
          <SbTooltip content="Zoom out">
            <TooltipTargetStyled>
              <SbButton buttonType="secondary" size="small" icon={<ZoomOutOutlined />} onClick={onZoomOut} disabled={zoom <= zoomMin} />
            </TooltipTargetStyled>
          </SbTooltip>
          <ZoomLevelStyled>{Math.round(zoom * 100)}%</ZoomLevelStyled>
          <SbTooltip content="Zoom in">
            <TooltipTargetStyled>
              <SbButton buttonType="secondary" size="small" icon={<ZoomInOutlined />} onClick={onZoomIn} disabled={zoom >= zoomMax} />
            </TooltipTargetStyled>
          </SbTooltip>
          <SbTooltip content="Reset zoom & position">
            <TooltipTargetStyled>
              <SbButton buttonType="secondary" size="small" icon={<UndoOutlined />} onClick={onResetView} disabled={isAtDefaultView} />
            </TooltipTargetStyled>
          </SbTooltip>
          <SbTooltip content="Save/Download PNG of this chart as shown">
            <TooltipTargetStyled>
              <SbButton buttonType="secondary" size="small" icon={<PictureOutlined />} onClick={handleExportPng} />
            </TooltipTargetStyled>
          </SbTooltip>
          <SbTooltip content={isFullscreen ? "Exit full page view" : "Expand to full page"}>
            <TooltipTargetStyled>
              <SbButton
                buttonType="secondary"
                size="small"
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => {
                  track(ANALYTICS_EVENTS.ORG_DESIGN.TREE_FULLSCREEN_TOGGLED, {
                    variant,
                    entering: !isFullscreen,
                  });
                  handleFullscreenToggle();
                }}
              />
            </TooltipTargetStyled>
          </SbTooltip>
        </ZoomControlsStyled>
      </OrgTreeCardHeaderStyled>
      <OrgTreeScrollCorrectionProvider value={scrollCorrection}>
        <ChartScrollAreaStyled ref={scrollAreaRef} $panning={isPanning} onMouseDown={handlePanStart}>
          <TreeZoomFrameStyled ref={frameRef}>
            <ZoomedTreeStyled ref={treeRef} $zoom={zoom} $animateZoom={animateZoom}>
              <OrgChartListStyled key={treeVersion}>
                {rootChildren.map((node) => (
                  <OrgDesignChartNode
                    key={node.id}
                    node={node}
                    editable={false}
                    onEdit={() => {}}
                    entryOption={entryOption}
                    expandedDepth={expandAll ? undefined : ORG_VIEW_EXPANDED_DEPTH}
                  />
                ))}
              </OrgChartListStyled>
            </ZoomedTreeStyled>
          </TreeZoomFrameStyled>
        </ChartScrollAreaStyled>
      </OrgTreeScrollCorrectionProvider>
    </OrgTreeCardStyled>
  );
});

OrgTreeCard.displayName = "OrgTreeCard";

export default OrgTreeCard;
